import { useNotifications } from '@risksmart-app/components/src/notifications/useNotifications';
import type { CauseByIdResponseRow } from '@risksmart-app/trpc/src/types';
import type { GetCauseByIdQuery } from '@risksmart-app/web-graphql-client/generated/graphql';
import { useQuery } from '@tanstack/react-query';
import { useEffect } from 'react';
import { useTRPC } from 'src/utils/trpc';

import { useIsFeatureVisibleToOrg } from '@/utils/featureFlags';

/**
 * Maps TRPC cause data to match the GraphQL query structure
 */
const mapTrpcCauseToGraphQL = (
  trpcData: CauseByIdResponseRow[]
): GetCauseByIdQuery => {
  return {
    cause: trpcData,
  };
};

export const useGetCauseById = (causeId: string, skip?: boolean) => {
  const trpcEnabled = useIsFeatureVisibleToOrg('trpc');
  const trpc = useTRPC();
  const { addNotification } = useNotifications();
  const {
    data: trpcData,
    isLoading: trpcLoading,
    refetch: trpcRefetch,
    error: trpcError,
  } = useQuery({
    ...trpc.frontend.cause.getById.queryOptions({ causeId: causeId }),

    enabled: trpcEnabled && !skip,
  });

  // Handle TRPC errors
  useEffect(() => {
    if (trpcEnabled && trpcError) {
      addNotification({
        type: 'error',
        content: <>{trpcError.message}</>,
      });
    }
  }, [trpcEnabled, trpcError, addNotification]);

  // Transform TRPC data to match the same structure as GraphQL query
  const data = trpcData ? mapTrpcCauseToGraphQL(trpcData) : undefined;

  return {
    loading: trpcLoading,
    data,
    refetch: trpcRefetch,
    error: trpcError,
  };
};
